import React from 'react';
import autoBind from 'react-autobind';
import { If, Then, Else } from 'react-if';
import Switch, { Case } from 'react-switch-case';
import { withTheme } from '@mui/styles';

import Config from "../../Config.jsx";
import Localization from "../../Localization.jsx";
import ReactAudioPlayer from 'react-audio-player';
import SubmissionDetailMindMap from "./SubmissionDetailMindMap.jsx";
import SettingsVoiceIcon from '@mui/icons-material/SettingsVoice';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ReplayIcon from '@mui/icons-material/Replay';

import {
  Icon,
  Chip,
  Avatar,
  Button,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  TextField,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Box
} from '@mui/material';

import {
  grey,
  green,
  red,
} from '@mui/material/colors';

const answerDefaultColor = grey[600];
const answerCorrectColor = green[600];
const answerWrongColor = red[600];

class SubmissionDetailContent extends React.Component {
  constructor(props) {
    super(props);

    // state variables here
    this.state = {
      answerData: this.props.answerData,
      componentData: this.props.componentData,
      transcription: '',
      loading: false,
      markingSchemeDialogOpen: false,
      correctAns: '',
      answerToExclude: '',
      otherGuidelines: '',
      aiAssessment: '',
      aiAssessmentResult: 'Loading...',
      showAiAssessment: false,
      submittingToAI: false,
      score: null,
      aiProcessedResponse: ''
    };
    autoBind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    // The share page is opened by someone with NO account, and every /api/ai
    // route is bearer-gated (`require_human`), so calling them here only
    // produced 401s and left the transcription block and the AI card
    // permanently empty for the person the link was sent to. The token-scoped
    // answer payload already carries both values, so read them off the row.
    if (!this.props.isSharedView) {
      const requestObj = { answer_id: this.state.answerData.id };
      this.props.root.refs.API.request("check_transcription", requestObj, this.checkTranscriptionOK, this.checkTranscriptionFailed);
    } else {
      const stored = this.state.answerData.voice_to_text_transcription;
      if (stored) {
        this.setState({ transcription: stored });
      }
    }

    const payloadString = (this.state.componentData.payload || '').replace(/[\n\r]/g, '');

    // `kind` used to arrive as a PHP string and was compared with
    // String(Config.HOTSPOT_CONTENT_*); the /api surface returns a native int,
    // so compare numerically or all three branches (marking scheme + AI
    // assessment on shared views) go dead.
    const componentKind = Number(this.state.componentData.kind);
    if (
      componentKind === Config.HOTSPOT_CONTENT_QUESTION ||
      componentKind === Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK ||
      componentKind === Config.HOTSPOT_CONTENT_TAKE_RECORD
    ) {
      try {
        const payload = JSON.parse(payloadString);
        this.setState({
          correctAns: payload.correctAnswer || '',
          answerToExclude: payload.answerToExclude || '',
          otherGuidelines: payload.otherGuidelines || '',
        });
      } catch (error) {
        console.error("Failed to parse payload:", error);
      }

      if (this.props.isSharedView) {
        // Same reason as the transcription above: /api/ai/latestAssessment is
        // bearer-gated, so the stored assessment comes with the shared answer.
        const stored = this.state.answerData.ai_assessment_result;
        if (stored) {
          this.setState({
            aiAssessmentResult: stored,
            showAiAssessment: true,
            aiProcessedResponse: stored
          });
          if (this.props.parent) {
            this.props.parent.setState({ score: this.extractScoreFromAIResult(stored) });
          }
        } else {
          this.setState({ aiAssessmentResult: "No assessment result available." });
        }
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    clearTimeout(this.timeoutId);
  }

  checkTranscriptionOK = (res) => {
    // res.body.result may be null/non-object on the OK path (common for answers with no
    // stored transcription), so read defensively before dereferencing .result.
    const r = res && res.body && res.body.result;
    const text = (r && typeof r === 'object') ? r.result : null;
    if (this._isMounted && text) {
      this.setState({ transcription: text });
    }
  }

  checkTranscriptionFailed = (err) => {
    console.error(err);
  }

  handleTranscriptionRequest = async () => {
    this.requestTranscription(false);
  }

  handleTranscriptionRerun = async () => {
    this.requestTranscription(true);
  }

  requestTranscription = (force = false) => {
    this.setState({ loading: true });

    // Set timeout for the request
    this.timeoutId = setTimeout(() => {
      if (!this._isMounted) {
        return;
      }
      this.setState({ loading: false });
      alert("Request timed out. Please try again.");
    }, 60000);

    var requestObj = { answer_id: this.state.answerData.id };
    if (force) {
      requestObj.force = 1;
    }
    this.props.root.refs.API.request("get_transcription", requestObj, this.getTranscriptionOK, this.getTranscriptionFailed);
  }

  getTranscriptionOK = (res) => {
    clearTimeout(this.timeoutId);
    if (!this._isMounted) {
      return;
    }
    const r = res && res.body && res.body.result;
    const text = (r && typeof r === 'object') ? r.result : '';
    this.setState({ transcription: text || '', loading: false });
  }

  getTranscriptionFailed = (err) => {
    clearTimeout(this.timeoutId);
    if (!this._isMounted) {
      return;
    }
    this.setState({ loading: false });
    alert("Failed to get transcription. Please try again.");
  }

  mcText(kind) {
    if (kind != Config.HOTSPOT_CONTENT_MC_OF_TEXTS) {
      return;
    }

    return this.mcTextMulti(Config.HOTSPOT_CONTENT_MC_OF_TEXTS_MULTI);
  }

  mcTextMulti(kind) {
    if (kind != Config.HOTSPOT_CONTENT_MC_OF_TEXTS_MULTI) {
      return;
    }

    let ans0, ans1, ans2, ans3;
    let correct0, correct1, correct2, correct3;
    let payload = '';
    try {
      payload = JSON.parse(this.state.componentData.payload.replace(/[\n\r]/g, ''));
    }
    catch (err) {
      console.log('the following payload causes the error:');
      for (let i = 0; i < this.state.componentData.payload.length; i++) {
        console.log(i.toString() + ': ' + this.state.componentData.payload[i].charCodeAt(0) + ' ,' + this.state.componentData.payload[i]);
      }
      console.log(err);
    }
    ans0 = (payload.ans0 != null ? payload.ans0 : "");
    ans1 = (payload.ans1 != null ? payload.ans1 : "");
    ans2 = (payload.ans2 != null ? payload.ans2 : "");
    ans3 = (payload.ans3 != null ? payload.ans3 : "");
    correct0 = (payload.correct0 != null ? payload.correct0 : false);
    correct1 = (payload.correct1 != null ? payload.correct1 : false);
    correct2 = (payload.correct2 != null ? payload.correct2 : false);
    correct3 = (payload.correct3 != null ? payload.correct3 : false);

    if (payload.correct0 == null) {
      correct0 = true;
    }

    let ansArr = [ans0, ans1, ans2, ans3];
    let correctAnwser = [correct0, correct1, correct2, correct3];
    let userAnswer = (this.state.answerData.answer || '').split(',');
    const userChoiceTag = Localization.strings.USER_CHOICE_TAG || "[User's choice]";

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', margin: '8px 0 12px 0' }}>
        {ansArr.map((row, i) => {
          let resultBG = answerDefaultColor;
          if (correctAnwser[i]) {
            resultBG = answerCorrectColor;
          }
          else if (userAnswer.includes(i.toString())) {
            resultBG = answerWrongColor;
          }

          return (
            <Chip
              key={i}
              style={{ margin: "4px 12px 4px 0", background: resultBG, color: 'White' }}
              avatar={
                <Avatar style={{ background: resultBG }}>
                  <Icon style={{ color: 'White' }}>{correctAnwser[i] ? "check_circle" : "highlight_off"}</Icon>
                </Avatar>
              }
              label={row + (userAnswer.includes(i.toString()) ? ` ${userChoiceTag}` : "")}
            />
          );
        })}
      </div>
    );
  }

  fillInTheBlank(kind) {
    if (kind != Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK) {
      return;
    }

    let payload = '';
    try {
      payload = JSON.parse(this.state.componentData.payload.replace(/[\n\r]/g, ''));
    }
    catch (err) {
      console.log('the following payload causes the error:');
      for (let i = 0; i < this.state.componentData.payload.length; i++) {
        console.log(i.toString() + ': ' + this.state.componentData.payload[i].charCodeAt(0) + ' ,' + this.state.componentData.payload[i]);
      }
      console.log(err);
    }
    let textBefore = (payload.text1 != null ? payload.text1 : "");
    let textAfter = (payload.text2 != null ? payload.text2 : "");
    return (
      textBefore + " ( ... ) " + textAfter
    );
  }

  customTable(kind) {
    if (kind != Config.HOTSPOT_CONTENT_TABLE_AND_CELL) {
      return;
    }

    let answer = [];
    if (this.state.answerData.answer != "") {
      try {
        answer = JSON.parse(this.state.answerData.answer);
        console.log("answer:", answer);
      }
      catch (err) {
        console.log(err);
        answer = [];
      }
    }

    let payload = '';
    try {
      payload = JSON.parse(this.state.componentData.payload.replace(/[\n\r]/g, ''));
    }
    catch (err) {
      console.log('the following payload causes the error:');
      for (let i = 0; i < this.state.componentData.payload.length; i++) {
        console.log(i.toString() + ': ' + this.state.componentData.payload[i].charCodeAt(0) + ' ,' + this.state.componentData.payload[i]);
      }
      console.log(err);
    }
    let columns = [], rows = [];
    if (payload != null) {
      columns = (payload.column_names != null ? payload.column_names.split(',') : []);
      rows = (payload.row_names != null ? payload.row_names.split(',') : []);
    }

    // A table with more columns than the viewport can hold is WIDER than this
    // component's column, and every ancestor up to the hotspot Card lets it
    // overflow — but MUI's Card is `overflow: hidden`, so the extra columns
    // were hard-clipped with nothing left to scroll (the page itself never
    // gained horizontal overflow either). That is invisible on a desktop
    // browser and fatal in the app's share-link WebView, where a phone-width
    // viewport cuts a 4-column table in half. TableContainer makes the table
    // its own horizontal scroll area so the overflow stops at the card border
    // and stays reachable by swiping.
    return (
      <TableContainer
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          // iOS WebView momentum scrolling for the swipe.
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Table padding="normal" border="1px" size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: grey[200] }}>
              <TableCell></TableCell>
              {
                columns.map((column, i) => {
                  return (
                    <TableCell key={i}>
                      {column}
                    </TableCell>
                  )
                })
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              rows.map((row, i) => {
                return (
                  <TableRow key={i}>
                    <TableCell style={{ backgroundColor: grey[200] }}>
                      {row}
                    </TableCell>
                    {
                      columns.map((dummy, j) => {
                        return (
                          <TableCell key={j}>
                            {answer[columns.length * i + j]}
                          </TableCell>
                        )
                      })
                    }
                  </TableRow>
                )
              })
            }
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  mcPic(kind) {
    if (kind != Config.HOTSPOT_CONTENT_MC_OF_PICTURES) {
      return;
    }

    return this.mcPicMulti(Config.HOTSPOT_CONTENT_MC_OF_PICTURES_MULTI);
  }

  mcPicMulti(kind) {
    if (kind != Config.HOTSPOT_CONTENT_MC_OF_PICTURES_MULTI) {
      return;
    }

    let payload = {};
    try {
      payload = JSON.parse(this.state.componentData.payload.replace(/[\n\r]/g, ''));
    }
    catch (err) {
      console.log(err);
    }

    let correct0, correct1, correct2, correct3;
    correct0 = (payload.correct0 != null ? payload.correct0 : false);
    correct1 = (payload.correct1 != null ? payload.correct1 : false);
    correct2 = (payload.correct2 != null ? payload.correct2 : false);
    correct3 = (payload.correct3 != null ? payload.correct3 : false);

    if (payload.correct0 == null) {
      correct0 = true;
    }

    let preview0, preview1, preview2, preview3;
    preview0 = (payload.picture0 != null ? Config.UPLOAD_PATH() + payload.picture0.tn_path : "");
    preview1 = (payload.picture1 != null ? Config.UPLOAD_PATH() + payload.picture1.tn_path : "");
    preview2 = (payload.picture2 != null ? Config.UPLOAD_PATH() + payload.picture2.tn_path : "");
    preview3 = (payload.picture3 != null ? Config.UPLOAD_PATH() + payload.picture3.tn_path : "");

    let previewArr = [preview0, preview1, preview2, preview3];
    let correctAnwser = [correct0, correct1, correct2, correct3];
    let userAnswer = (this.state.answerData.answer || '').split(',');
    const userChoiceTag = Localization.strings.USER_CHOICE_TAG || "[User's choice]";

    return (
      <div style={{ maxWidth: "100%" }}>
        <ImageList
          style={{ flex: "1", overflow: 'auto' }}
        >
          {previewArr.map((tile, i) => {
            let resultBG = answerDefaultColor;
            if (correctAnwser[i]) {
              resultBG = answerCorrectColor;
            }
            else if (userAnswer.includes(i.toString())) {
              resultBG = answerWrongColor;
            }

            let marginRight = (userAnswer.includes(i.toString()) ? "12px" : "0px");

            return (
              <ImageListItem key={i}>
                <img alt="" src={tile} />
                <ImageListItemBar
                  title={
                    <Chip
                      style={{ marginRight: marginRight, background: resultBG, color: 'White' }}
                      avatar={
                        <Avatar style={{ background: resultBG }}>
                          <Icon style={{ color: 'White' }}>{correctAnwser[i] ? "check_circle" : "highlight_off"}</Icon>
                        </Avatar>
                      }
                      label={(userAnswer.includes(i.toString()) ? ` ${userChoiceTag}` : "")}
                    />
                  }
                />
              </ImageListItem>
            );
          })}
        </ImageList>
      </div>
    );
  }

  mindMap(kind) {
    if (kind != Config.HOTSPOT_CONTENT_FILL_IN_MIND_MAP) {
      return;
    }

    return (
      <SubmissionDetailMindMap root={this.props.root} parent={this}
        componentData={this.props.componentData}
      />
    );
  }

  npcConversation(kind) {
    if (kind != Config.HOTSPOT_CONTENT_NPC) {
      return;
    }

    let conversationHistory = [];
    
    // Parse the answer field which should contain JSON array of conversation messages
    if (this.state.answerData.answer && this.state.answerData.answer.trim() !== '') {
      try {
        const parsed = JSON.parse(this.state.answerData.answer);
        if (Array.isArray(parsed)) {
          // Filter out system messages and only show user and assistant messages
          conversationHistory = parsed.filter(msg => 
            msg.role === 'user' || msg.role === 'assistant'
          );
        }
      } catch (error) {
        console.error("Failed to parse NPC conversation:", error);
      }
    }

    if (conversationHistory.length === 0) {
      return (
        <div style={{ marginTop: '10px', 
                color: answerWrongColor, 
                backgroundColor: '#EEEEEE', 
                borderRadius: '10px', 
                padding: '10px',
                maxWidth: '600px'}}>
            {Localization.strings.NO_ANSWER || 'No conversation history'}
        </div>
      );
    }

    return (
      <div style={{ 
        marginTop: '10px',
        maxWidth: '800px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {conversationHistory.map((msg, index) => {
          const isUser = msg.role === 'user';
          return (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isUser ? 'flex-end' : 'flex-start',
                gap: '4px'
              }}
            >
              <div
                style={{
                  backgroundColor: isUser ? '#e3f2fd' : '#f5f5f5',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  maxWidth: '70%',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  wordWrap: 'break-word',
                  whiteSpace: 'pre-wrap'
                }}
              >
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '4px',
                  fontWeight: 'bold'
                }}>
                  {isUser ? (Localization.strings.USER || 'User') : (Localization.strings.ASSISTANT || 'Assistant')}
                </div>
                <div style={{
                  color: '#333',
                  lineHeight: '1.5'
                }}>
                  {msg.content || ''}
                </div>
                {msg.timestamp && (
                  <div style={{
                    fontSize: '10px',
                    color: '#999',
                    marginTop: '4px',
                    textAlign: 'right'
                  }}>
                    {new Date(msg.timestamp).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  handleOpenMarkingSchemeDialog = () => {
    console.log("SubmissionDetailContent.handleOpenMarkingSchemeDialog called");
    console.log("isSharedView:", this.props.isSharedView);
    this.setState({ markingSchemeDialogOpen: true, showAiAssessment: false });
  
    // Load latest AI assessment if available
    this.props.root.refs.API.request(
      "get_latest_ai_assessment", 
      { answer_id: this.state.answerData.id }, 
      this.getLatestAiAssessmentResultOK, 
      this.getLatestAiAssessmentResultFailed
    );
    
    // Check score
    this.props.root.refs.API.request(
      "get_answer_score",
      { answer_id: this.state.answerData.id },
      this.getAnswerScoreOK,
      this.getAnswerScoreFailed
    );
    
    // For empty answers, automatically load NO_ANSWER_ASSESSMENT text
    if (!this.state.answerData.answer || this.state.answerData.answer.trim() === "") {
      this.setState({
        aiAssessmentResult: Localization.strings.NO_ANSWER_ASSESSMENT || 'Student did not provide an answer. According to the marking scheme, no answer will receive 0 marks.\n\nSuggestions:\n1. Please ensure you answer all questions\n2. Even if unsure, try to provide an answer\n3. Discuss with teacher if unsure\n\nMark: <0%>',
        showAiAssessment: true,
        score: "0%"
      });
    }
  }

  getAnswerScoreOK = (res) => {
    console.log("Score response:", res); // Debug log
    if (res && res.body && res.body.result && Array.isArray(res.body.result) && res.body.result[0]) {
      const score = res.body.result[0].score;
      // Convert score to number and check if it's -1 (no score)
      const numScore = Number(score);
      if (numScore === -1) {
        this.setState({ score: null });
        console.log("No score set yet");
      } else {
        this.setState({ score: numScore });
        console.log("Setting score to:", numScore);
      }
    } else {
      console.log("No score available in response");
      this.setState({ score: null });
    }
  }

  getAnswerScoreFailed = (err) => {
    console.error("Failed to get answer score:", err);
    this.setState({ score: null });
  }

  getLatestAiAssessmentResultOK = (res) => {
    if (!res.body || !res.body.result) {
      this.setState({ aiAssessmentResult: "No assessment result available." });
      console.log("aiAssessmentResult: No assessment result available.");
    } else {
      const raw = res.body.result;
      const result = (typeof raw === 'string')
        ? raw
        : (raw.result || (raw.ai_processed_item && raw.ai_processed_item.response) || '');
      const aiProcessedResponse = (typeof raw === 'object' && raw && raw.ai_processed_item && raw.ai_processed_item.response) ? raw.ai_processed_item.response : '';
      this.setState({ 
        aiAssessmentResult: result,
        showAiAssessment: true,
        aiProcessedResponse
      });
      // Pass the result to parent component
      if (this.props.parent) {
        this.props.parent.setState({ score: this.extractScoreFromAIResult(result) });
      }
      console.log("aiAssessmentResult:", result);
    }
  }

  getLatestAiAssessmentResultFailed = (err) => {
    console.error("Failed to get latest AI assessment result:", err);
    this.setState({ aiAssessmentResult: "Failed to retrieve assessment result." });
  }

  submitToAIOK = (res) => {
    if (res.body && res.body.result) {
      const raw = res.body.result;
      if (typeof raw === 'object' && raw && raw.error) {
        console.error("AI assessment error:", raw.error);
        this.setState({
          aiAssessmentResult: raw.error,
          showAiAssessment: true,
          submittingToAI: false
        });
        return;
      }
      const result = (typeof raw === 'string')
        ? raw
        : (raw.result || (raw.ai_processed_item && raw.ai_processed_item.response) || '');
      const aiProcessedResponse = (typeof raw === 'object' && raw && raw.ai_processed_item && raw.ai_processed_item.response) ? raw.ai_processed_item.response : '';
      console.log("aiAssessment:", result);
      this.setState({ 
        aiAssessment: result, 
        aiAssessmentResult: result,
        showAiAssessment: true, 
        markingSchemeDialogOpen: true,
        submittingToAI: false,
        aiProcessedResponse
      });
      // Pass the result to parent component
      if (this.props.parent) {
        this.props.parent.setState({ score: this.extractScoreFromAIResult(result) });
      }
    } else {
      console.error("Invalid AI assessment response:", res);
      this.setState({
        aiAssessmentResult: "Failed to get AI assessment result.",
        submittingToAI: false
      });
    }
  }

  submitToAIFailed = (err) => {
    console.error("Submit to AI failed:", err);
    this.setState({ submittingToAI: false });
  }

  extractScoreFromAIResult = (aiResult) => {
    if (!aiResult) return null;
    const match = aiResult.match(/<(\d+)[%％]>/);  // Match both % and ％ symbols
    return match ? match[1] + '%' : null;
  }

  handleScoreUpdate = (newScore) => {
    // Validate score is a number and within range
    const scoreNum = Number(newScore);
    if (isNaN(scoreNum) || scoreNum < 0 || scoreNum > 100) {
      console.error("Invalid score value:", newScore);
      return;
    }

    this.props.root.refs.API.request(
      "edit_answer_score",
      { 
        answer_id: this.state.answerData.id,
        score: scoreNum
      },
      (res) => {
        if (res.body && res.body.success) {
          this.setState({ score: scoreNum });
          console.log("Score updated successfully to:", scoreNum);
        } else {
          console.error("Failed to update score:", res.body && res.body.error);
          // Revert to previous score on failure
          this.props.root.refs.API.request(
            "get_answer_score",
            { answer_id: this.state.answerData.id },
            this.getAnswerScoreOK,
            this.getAnswerScoreFailed
          );
        }
      },
      (err) => {
        console.error("Failed to update score:", err);
        this.setState({ submittingToAI: false });
      }
    );
  }

  handleCloseMarkingSchemeDialog = () => {
    this.setState({ markingSchemeDialogOpen: false });
  }
  
  handleSubmitToAI = () => {
    this.setState({ submittingToAI: true}); 
    this.props.root.refs.API.request(
      "get_ai_assessment_v2",
      { answer_id: this.state.answerData.id },
      this.submitToAIOK,
      this.submitToAIFailed
    );
  }
  
  render() {
    let kind = parseInt(this.state.answerData.kind);
    let name = (this.state.componentData.name != null ? this.state.componentData.name : "");
    let content = (this.state.componentData.content != null ? this.state.componentData.content : "");
  
    if (name == "") {
      name = Config.GET_HOTSPOT_CONTENT_CONFIG(kind).title;
    }
  
    return (
      <div>
        <Switch condition={kind}>
          <Case value={Config.HOTSPOT_CONTENT_MC_OF_TEXTS}>
            {this.mcText(kind)}
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_MC_OF_PICTURES}>
            {this.mcPic(kind)}
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_MC_OF_TEXTS_MULTI}>
            {this.mcTextMulti(kind)}
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_MC_OF_PICTURES_MULTI}>
            {this.mcPicMulti(kind)}
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_QUESTION}>
            {this.state.answerData.answer && this.state.answerData.answer.trim() !== '' ? (
              <div>{this.state.answerData.answer}</div>
            ) : (
              <div style={{ marginTop: '10px', 
                      color: answerWrongColor, 
                      backgroundColor: '#EEEEEE', 
                      borderRadius: '10px', 
                      padding: '10px',
                      maxWidth: '300px'}}>
                  {Localization.strings.NO_ANSWER || 'Not Answered'}
              </div>
            )}
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK}>
            {this.state.answerData.answer && this.state.answerData.answer.trim() !== '' ? (
              <div>{this.state.answerData.answer}</div>
            ) : (
              <div style={{ marginTop: '10px', 
                      color: answerWrongColor, 
                      backgroundColor: '#EEEEEE', 
                      borderRadius: '10px', 
                      padding: '10px',
                      maxWidth: '300px'}}>
                  {Localization.strings.NO_ANSWER || 'Not Answered'}
              </div>
            )}
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_FILL_IN_MIND_MAP}>
            {this.mindMap(kind)}
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_3D_MODEL}>
             <div style={{ marginTop: '10px', 
                      color: answerDefaultColor, 
                      backgroundColor: '#EEEEEE', 
                      borderRadius: '10px', 
                      padding: '10px',
                      maxWidth: '300px'}}>
                  {Localization.strings.NO_ANSWER_NEEDED}
              </div>
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_TAKE_PHOTO}>
            <If condition={this.state.answerData.path != ""}>
              <Then>
                <img 
                  alt="" 
                  src={Config.UPLOAD_PATH() + this.state.answerData.path} 
                  style={{ maxWidth: '100%', height: 'auto' }} 
                />
              </Then>
              <Else>
              <div style={{ marginTop: '10px', 
                      color: answerWrongColor, 
                      backgroundColor: '#EEEEEE', 
                      borderRadius: '10px', 
                      padding: '10px',
                      maxWidth: '300px'}}>
                  {Localization.strings.NO_PHOTO}
                </div>
              </Else>
            </If>
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_TABLE_AND_CELL}>
            {this.customTable(kind)}
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_NPC}>
            {this.npcConversation(kind)}
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_DRAWING_TASK}>
            <If condition={this.state.answerData.path != ""}>
              <Then>
                <img 
                  alt="" 
                  src={Config.UPLOAD_PATH() + this.state.answerData.path} 
                  style={{ maxWidth: '100%', height: 'auto' }} 
                />
              </Then>
              <Else>
              <div style={{ marginTop: '10px', 
                      color: answerWrongColor, 
                      backgroundColor: '#EEEEEE', 
                      borderRadius: '10px', 
                      padding: '10px',
                      maxWidth: '300px'}}>
                  {Localization.strings.NO_DRAWING}
                </div>
              </Else>
            </If>
          </Case>
          <Case value={Config.HOTSPOT_CONTENT_TAKE_RECORD}>
            <If condition={this.state.answerData.path != ""}>
              <Then>
                <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', width: '300px' }}>
                  <ReactAudioPlayer
                    src={Config.UPLOAD_PATH() + this.state.answerData.path}
                    autoPlay={false}
                    loop={false}
                    controls={true}
                    preload={"auto"}
                    style={{ width: '100%' }}
                  />
                  <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-start' }}>
                  {!this.props.isSharedView && (
                  <>
                  {this.state.loading ? (
                    <CircularProgress size={48} style={{ color: 'primary', marginLeft: '20px', width:'32px', height: '32px' }} />
                  ) : this.state.transcription ? (
                    <div style={{ marginTop: '10px', color: 'black' }}>
                      {/* Re-run button is shown together with the transcription result below */}
                    </div>
                  ) : (
                    <Tooltip title={this.props.root.getPrivilege('AI_VOICE_TO_TEXT').value === '1' ? Localization.strings.VOICE_TO_TEXT : (Localization.strings.UPGRADE_TO_PREMIUM || '目前功能受訂閱方案限制，建議升級以解鎖更多功能')}>
                      <span>
                        <Button
                          onClick={this.handleTranscriptionRequest}
                          variant="contained"
                          color="primary"
                          style={{
                            marginTop: '8px',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px', // Ensure the button maintains its aspect ratio
                            minHeight: '32px' // Ensure the button maintains its aspect ratio
                          }}
                          disabled={this.state.loading || this.props.root.getPrivilege('AI_VOICE_TO_TEXT').value !== '1'}
                        >
                          <SettingsVoiceIcon style={{ color: 'white' }} />
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  </>
                  )}
                  </div>
                </div>


                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {this.state.transcription ? (
                    <div style={{ 
                      marginTop: '10px', 
                      color: 'black', 
                      backgroundColor: '#f1f3f4', 
                      borderRadius: '10px', 
                      padding: '10px',
                      maxWidth: '300px',
                      width: '300px',
                      boxSizing: 'border-box'
                    }}>
                      <div style={{ fontWeight: 'bold',
                           
                      }}>
                        {Localization.strings.AI_TEXT_TRANSCRIPTION} 
                      </div>
                      {!this.props.isSharedView && (
                        <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                          <Button
                            onClick={this.handleTranscriptionRerun}
                            size="small"
                            variant="outlined"
                            startIcon={<ReplayIcon />}
                            disabled={this.state.loading || this.props.root.getPrivilege('AI_VOICE_TO_TEXT').value !== '1'}
                            style={{ textTransform: 'none' }}
                          >
                            {Localization.strings.RE_RUN_VOICE_TO_TEXT || Localization.strings.RE_RUN_AI_ASSESSMENT}
                          </Button>
                        </div>
                      )}
                      <br></br>
                      {this.state.transcription}
                    </div>
                    ) : (
                    <div style={{ marginTop: '10px', color: 'black' }}>
                      {/* Empty div for no transcription */}
                    </div>
                  )}
                </div>

              </Then>
              <Else>
              <div style={{ marginTop: '10px', 
                      color: answerWrongColor, 
                      backgroundColor: '#EEEEEE', 
                      borderRadius: '10px', 
                      padding: '10px',
                      maxWidth: '300px'}}>
                  {Localization.strings.NO_RECORDING}
                </div>

              </Else>
            </If>
          </Case>
        </Switch>

        {this.props.isSharedView && this.state.showAiAssessment &&
         this.state.answerData.answer && this.state.answerData.answer.trim() !== '' &&
         this.state.aiAssessmentResult && this.state.aiAssessmentResult !== '-1' && (
          <Card sx={{ mt: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <SmartToyIcon sx={{ color: '#ad1179', mr: 1, fontSize: '20px' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#ad1179' }}>
                  {Localization.strings.AI_ASSESSMENT || 'AI Assessment'}
                </Typography>
                {this.extractScoreFromAIResult(this.state.aiAssessmentResult) && (
                  <Box sx={{
                    ml: 'auto',
                    backgroundColor: 'rgba(173, 17, 121, 0.1)',
                    borderRadius: '12px',
                    px: 1.5,
                    py: 0.25,
                  }}>
                    <Typography variant="body2" sx={{
                      color: '#ad1179',
                      fontWeight: 'bold',
                    }}>
                      {this.extractScoreFromAIResult(this.state.aiAssessmentResult)}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  backgroundColor: '#f9f9f9',
                  p: 1.5,
                  borderRadius: '6px',
                  lineHeight: 1.6,
                  color: 'text.secondary',
                }}
              >
                {this.state.aiAssessmentResult}
              </Typography>
            </CardContent>
          </Card>
        )}

        {!this.props.isSharedView && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 100090, pointerEvents: "none" }}>
          <Dialog
            open={this.state.markingSchemeDialogOpen}
            onClose={this.handleCloseMarkingSchemeDialog}
            aria-labelledby="form-dialog-title"
            maxWidth="md"
            fullWidth
            sx={{
              zIndex: 100100,
              '& .MuiBackdrop-root': {
                zIndex: 100095
              },
              '& .MuiDialog-paper': {
                zIndex: 100100
              },
              pointerEvents: "auto"
            }}
          >
            <DialogTitle id="form-dialog-title" style={{ 
              backgroundColor: this.props.theme.palette.primary.main, 
              color: 'white', 
              marginBottom: '15px',
              fontSize: '1.5rem', 
              fontWeight: 'bold' 
            }}>
              {Localization.strings.MARKING_SCHEME_AND_AI_ASSESSMENT}
            </DialogTitle>
            <DialogContent>
              {/* Marking Scheme Section */}
              <Card style={{ marginBottom: '20px' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {Localization.strings.MARKING_SCHEME}
                  </Typography>
                  
                  {/* Correct Answers and Wrong Answers side by side */}
                  <Box sx={{ display: 'flex', marginBottom: '20px', flexDirection: { xs: 'column', sm: 'row' } }}>
                    {/* Correct Answers */}
                    <Box sx={{ 
                      width: { xs: '100%', sm: '50%' }, 
                      pr: { xs: 0, sm: 1 },
                      mb: { xs: 2, sm: 0 }
                    }}>
                      <Typography variant="subtitle1" color="primary" gutterBottom>
                        {Localization.strings.CORRECT_ANSWER}:
                      </Typography>
                      <Box sx={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '12px', 
                        borderRadius: '4px',
                        minHeight: '120px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                          {this.state.correctAns || Localization.strings.NO_ANSWER}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Wrong Answers to Exclude */}
                    <Box sx={{ 
                      width: { xs: '100%', sm: '50%' }, 
                      pl: { xs: 0, sm: 1 }
                    }}>
                      <Typography variant="subtitle1" color="error" gutterBottom>
                        {Localization.strings.ANSWER_TO_EXCLUDE}:
                      </Typography>
                      <Box sx={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '12px', 
                        borderRadius: '4px',
                        minHeight: '120px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                          {this.state.answerToExclude || Localization.strings.NO_ANSWER}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Other Guidelines - as a separate row below */}
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                      {Localization.strings.OTHER_GUIDELINES}:
                    </Typography>
                    <Box sx={{ 
                      backgroundColor: '#f5f5f5', 
                      padding: '12px', 
                      borderRadius: '4px',
                      minHeight: '70px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                        {this.state.otherGuidelines || Localization.strings.NO_ADDITIONAL_GUIDELINES}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* AI Assessment Section */}
              <Card style={{ marginBottom: '20px', boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {Localization.strings.ASSESSMENT}
                  </Typography>

                  {/* Score Display Section */}
                  {(this.state.showAiAssessment || (!this.state.answerData.answer || this.state.answerData.answer.trim() === '')) && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      marginBottom: '16px',
                      width: '100%'
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        backgroundColor: '#f8f8f8', 
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        minWidth: '200px'
                      }}>
                        {/* Original Student Answer */}
                        <Box sx={{ flex: 1, marginRight: '16px' }}>
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#555', marginBottom: '8px' }}>
                            {Localization.strings.STUDENT_ANSWER}:
                          </Typography>
                          <Typography 
                            variant="body1" 
                            style={{ 
                              whiteSpace: 'pre-wrap', 
                              backgroundColor: 'white', 
                              padding: '8px', 
                              borderRadius: '4px',
                              border: '1px solid #e0e0e0',
                              lineHeight: '1.4'
                            }}
                          >
                            {
                              // For TAKE_RECORD, show AI processed response first, then transcription, instead of file path
                              parseInt(this.state.componentData.kind) === Config.HOTSPOT_CONTENT_TAKE_RECORD
                                ? (
                                    (this.state.aiProcessedResponse && this.state.aiProcessedResponse.trim() !== '')
                                      ? this.state.aiProcessedResponse
                                      : (
                                          this.state.transcription && this.state.transcription.trim() !== ''
                                            ? this.state.transcription
                                            : (Localization.strings.NO_ANSWER)
                                        )
                                  )
                                : (
                                    !this.state.answerData.answer || this.state.answerData.answer.trim() === ''
                                      ? Localization.strings.NO_ANSWER
                                      : this.state.answerData.answer
                                  )
                            }
                          </Typography>
                        </Box>

                        {/* AI Score */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#555', marginBottom: '8px' }}>
                            {Localization.strings.AI_SCORE}:
                          </Typography>
                          <Typography 
                            variant="h4" 
                            style={{ 
                              color: '#ad1179',
                              fontWeight: 'bold',
                              backgroundColor: 'rgba(173, 17, 121, 0.1)', 
                              padding: '4px 16px', 
                              borderRadius: '20px',
                              display: 'inline-block'
                            }}
                          >
                            {(!this.state.answerData.answer || this.state.answerData.answer.trim() === '') ? 
                              '0%' : 
                              (this.extractScoreFromAIResult(this.state.aiAssessmentResult) || 'N/A')}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* AI Assessment Text */}
                  <Box sx={{ 
                    marginBottom: '16px',
                    backgroundColor: '#f8f8f8', 
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#555', marginBottom: '8px' }}>
                      {Localization.strings.AI_ASSESSMENT}:
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="textSecondary" 
                      style={{ 
                        whiteSpace: 'pre-wrap', 
                        backgroundColor: 'white', 
                        padding: '12px', 
                        borderRadius: '4px',
                        border: '1px solid #e0e0e0',
                        lineHeight: '1.6'
                      }}
                    >
                      {!this.state.answerData.answer || this.state.answerData.answer.trim() === '' ? 
                        Localization.strings.NO_ANSWER_ASSESSMENT :
                        (this.state.showAiAssessment ? this.state.aiAssessmentResult : Localization.strings.CLICK_GET_AI_ASSESSMENT)}
                    </Typography>
                  </Box>
                  
                  {/* Submit to AI Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      onClick={this.handleSubmitToAI}
                      variant="contained"
                      disabled={this.state.submittingToAI}
                      sx={{
                        backgroundColor: '#ad1179',
                        '&:hover': {
                          backgroundColor: 'rgba(173, 17, 121, 0.8)'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(173, 17, 121, 0.4)'
                        }
                      }}
                    >
                      {this.state.submittingToAI 
                        ? <CircularProgress size={20} color="inherit" /> 
                        : (
                            <>
                              <ReplayIcon style={{ marginRight: 8 }} />
                              {Localization.strings.RE_RUN_AI_ASSESSMENT}
                            </>
                          )}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleCloseMarkingSchemeDialog} color="primary">
                {Localization.strings.CLOSE}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
        )}
      </div>
    );
  }
}

export default withTheme(SubmissionDetailContent);